import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../axios";
import { toast } from "react-toastify";
import { MdDelete, MdEdit, MdLeaderboard, MdPeople } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import DeletePasswordModal from "../../../componant/DeletePasswordModal";

const initialForm = {
  title: "",
  titleHin: "",
  description: "",
  course: "",
  exam: "",
  category: "",
  durationMinutes: "",
  totalQuestions: 100,
  subjectDistribution: [{ subject: "", questionCount: "" }],
  isActive: true,
};

const emptySubject = { subject: "", questionCount: "" };

const getId = (value) => value?._id || value || "";

const getExamName = (exam) =>
  exam?.examName || exam?.name || exam?.title || "";

// eslint-disable-next-line no-unused-vars
const sampleQuestions = [
  {
    question: "What is the capital of India?",
    questionHin: "भारत की राजधानी क्या है?",
    options: ["Delhi", "Mumbai", "Kolkata", "Chennai"],
    optionsHin: ["दिल्ली", "मुंबई", "कोलकाता", "चेन्नई"],
    answer: "Delhi",
    answerHin: "दिल्ली",
    quesType: "gk",
  },
];

export default function MockTests() {
  const { courseId } = useParams();
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [mockTests, setMockTests] = useState([]);
  const [form, setForm] = useState({ ...initialForm, course: courseId || "" });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("create");
  const [deleteMockTestId, setDeleteMockTestId] = useState(null);

  const selectedRouteCourse = useMemo(
    () => courses.find((course) => course._id === courseId),
    [courses, courseId]
  );

  const selectedCourse = useMemo(
    () => courses.find((course) => course._id === form.course),
    [courses, form.course]
  );

  const courseExamOptions = useMemo(() => {
    // const courseExams = Array.isArray(selectedCourse?.exams)
    //   ? selectedCourse.exams
    //   : [];
    const apiExams = exams.filter((exam) => {
      const examCourseId = getId(exam.course);

      return !form.course || !examCourseId || examCourseId === form.course;
    });
    const optionsById = new Map();

    [ ...apiExams].forEach((exam) => {
      const examId = getId(exam);
      const examName = getExamName(exam);

      if (examId && examName && !optionsById.has(examId)) {
        optionsById.set(examId, { ...exam, _id: examId, examName });
      }
    });

    return Array.from(optionsById.values());
  }, [exams, form.course, selectedCourse]);

  const examNameById = useMemo(
    () =>
      [...exams, ...courses.flatMap((course) => course.exams || [])].reduce((acc, exam) => {
        const examId = getId(exam);
        const examName = getExamName(exam);

        if (examId && examName) {
          acc[examId] = examName;
        }

        return acc;
      }, {}),
    [courses, exams]
  );

  const filteredMockTests = useMemo(() => {
    const value = search.trim().toLowerCase();
    const courseMockTests = courseId
      ? mockTests.filter((mockTest) => {
          const mockCourseId = mockTest.course?._id || mockTest.course || "";

          return mockCourseId === courseId;
        })
      : mockTests;

    if (!value) return courseMockTests;

    return courseMockTests.filter((mockTest) => {
      const examName =
        getExamName(mockTest.examDetails) ||
        getExamName(mockTest.exam) ||
        examNameById[getId(mockTest.exam)] ||
        mockTest.course?.exams?.find((exam) => getId(exam) === getId(mockTest.exam))
          ?.examName ||
        "";

      return [
        mockTest.title,
        mockTest.category,
        mockTest.course?.planName,
        examName,
      ].some((item) => item?.toLowerCase().includes(value));
    });
  }, [mockTests, search, courseId, examNameById]);

  useEffect(() => {
    fetchCourses();
    fetchExams();
    fetchMockTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;

    setForm((prev) => ({
      ...prev,
      course: courseId,
      exam: prev.course === courseId ? prev.exam : "",
    }));
    setActiveTab("create");
  }, [courseId]);

  useEffect(() => {
    if (!form.course || !courseExamOptions.length) return;

    const isSelectedExamValid = courseExamOptions.some(
      (exam) => getId(exam) === getId(form.exam)
    );

    if (isSelectedExamValid) return;

    setForm((prev) => ({
      ...prev,
      exam: getId(courseExamOptions[0]),
    }));
  }, [courseExamOptions, form.course, form.exam]);

  const fetchCourses = async () => {
    try {
      const res = await axios.get("/getAllCoursesForAdmin");
      setCourses(res.data?.result || []);
    } catch (error) {
      toast.error("Courses load nahi ho paye");
    }
  };

  const fetchExams = async () => {
    try {
      const res = await axios.get("/exams/all");
      setExams(res.data?.result || res.data?.data || []);
    } catch (error) {
      setExams([]);
      toast.error("Exams load nahi ho paye");
    }
  };

  const fetchMockTests = async () => {
    try {
      const endpoint = courseId
        ? `/mock-tests/?courseId=${courseId}`
        : "/mock-tests/admin/get-mock-tests";
      const res = await axios.get(endpoint);
      setMockTests(res.data?.result || res.data?.data || []);
    } catch (error) {
      setMockTests([]);
      toast.error("Mock tests load nahi ho paye");
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "course" ? { exam: "" } : {}),
    }));
  };

  const handleSubjectChange = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      subjectDistribution: prev.subjectDistribution.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addSubjectRow = () => {
    setForm((prev) => ({
      ...prev,
      subjectDistribution: [...prev.subjectDistribution, emptySubject],
    }));
  };

  const removeSubjectRow = (index) => {
    setForm((prev) => ({
      ...prev,
      subjectDistribution:
        prev.subjectDistribution.length === 1
          ? [emptySubject]
          : prev.subjectDistribution.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const resetForm = () => {
    setForm({ ...initialForm, course: courseId || "" });
    setEditingId(null);
  };

  const buildPayload = () => ({
    ...form,
    durationMinutes: Number(form.durationMinutes),
    totalQuestions: Number(form.totalQuestions),
    subjectDistribution: form.subjectDistribution
      .filter((item) => item.subject && item.questionCount)
      .map((item) => ({
        subject: item.subject,
        questionCount: Number(item.questionCount),
      })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = buildPayload();

      if (editingId) {
        await axios.put(`/mock-tests/${editingId}`, payload);
        toast.success("Mock test updated successfully");
      } else {
        await axios.post(`/mock-tests`, payload);
        toast.success("Mock test created successfully");
      }

      resetForm();
      fetchMockTests();
      setActiveTab("view");
    } catch (error) {
      toast.error(error.response?.data?.message || "Mock test save nahi ho paya");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (mockTest) => {
    setEditingId(mockTest._id);
    setForm({
      title: mockTest.title || "",
      titleHin: mockTest.titleHin || "",
      description: mockTest.description || "",
      course: getId(mockTest.course) || courseId || "",
      exam: getId(mockTest.exam),
      category: mockTest.category || "",
      durationMinutes: mockTest.durationMinutes || "",
      totalQuestions: mockTest.totalQuestions || 100,
      subjectDistribution: mockTest.subjectDistribution?.length
        ? mockTest.subjectDistribution
        : [{ subject: "", questionCount: "" }],
      isActive: mockTest.isActive !== false,
    });
    setActiveTab("create");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/mock-tests/${id}`);
      toast.success("Mock test deleted successfully");
      fetchMockTests();
    } catch (error) {
      toast.error(error.response?.data?.message || "Mock test delete nahi ho paya");
    }
  };

  return (
    <div className="min-h-screen bg-bgWhite p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-oswald text-2xl font-bold text-purple">
            {courseId ? ` ${selectedRouteCourse?.planName } Mock Tests ` : "Mock Test Controls"}
          </h2>
          <p className="text-sm text-gray-600">
            {courseId
              ? `Create and manage mock tests for ${selectedRouteCourse?.planName || "this course"}.`
              : "Create, edit and manage mock tests."}
          </p>
        </div>
      </div>

      <div className="mb-5 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            className={`rounded-md px-4 py-3 text-sm font-semibold transition ${
              activeTab === "create"
                ? "bg-purple text-white shadow"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("create")}
          >
            {editingId ? "Edit Mock Test" : "Create Mock Test"}
          </button>
          <button
            type="button"
            className={`rounded-md px-4 py-3 text-sm font-semibold transition ${
              activeTab === "view"
                ? "bg-purple text-white shadow"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => {
              setActiveTab("view");
              fetchMockTests();
            }}
          >
            View Mock Test
          </button>
        </div>
      </div>

      {activeTab === "create" ? (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-bgBlue">
                {editingId ? "Edit Mock Test" : "Create Mock Test"}
              </h3>
              <p className="text-sm text-gray-600">
                Course, exam, duration aur question distribution set karein.
              </p>
            </div>

            {editingId && (
              <button
                type="button"
                className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                onClick={resetForm}
              >
                Cancel Edit
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">Title</label>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Title Hindi</label>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2"
              name="titleHin"
              value={form.titleHin}
              onChange={handleChange}
            />
          </div>

          {courseId ? (
            <div>
              <label className="mb-1 block text-sm font-medium">Course</label>
              <input
                className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2"
                value={selectedRouteCourse?.planName || "Selected Course"}
                readOnly
              />
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium">Course</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2"
                name="course"
                value={form.course}
                onChange={handleChange}
                required
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.planName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">Exam</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2"
              name="exam"
              value={form.exam}
              onChange={handleChange}
              required
              disabled={!form.course}
            >
              <option value="">Select Exam</option>
              {courseExamOptions.map((exam) => (
                <option key={exam._id} value={exam._id}>
                  {exam.examName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Category</label>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2"
              name="category"
              value={form.category}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Duration Minutes</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2"
                min="1"
                name="durationMinutes"
                type="number"
                value={form.durationMinutes}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Total Questions</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2"
                min="1"
                name="totalQuestions"
                type="number"
                value={form.totalQuestions}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <label className="mb-1 block text-sm font-medium">Description</label>
            <textarea
              className="min-h-24 w-full rounded border border-gray-300 px-3 py-2"
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          <div className="lg:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium">
                Subject Distribution
              </label>
              <button
                type="button"
                className="rounded bg-bgBlue px-3 py-1 text-sm text-white"
                onClick={addSubjectRow}
              >
                + Add Subject
              </button>
            </div>

            <div className="space-y-2">
              {form.subjectDistribution.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2">
                  <input
                    className="col-span-7 rounded border border-gray-300 px-3 py-2"
                    placeholder="Subject"
                    value={item.subject}
                    onChange={(e) =>
                      handleSubjectChange(index, "subject", e.target.value)
                    }
                  />
                  <input
                    className="col-span-3 rounded border border-gray-300 px-3 py-2"
                    min="1"
                    placeholder="Count"
                    type="number"
                    value={item.questionCount}
                    onChange={(e) =>
                      handleSubjectChange(index, "questionCount", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="col-span-2 rounded border border-red-300 px-2 py-2 text-sm text-red-600"
                    onClick={() => removeSubjectRow(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />
            Active
          </label>

          <div className="flex gap-2 lg:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-purple px-5 py-2 font-semibold text-white disabled:opacity-70"
            >
              {loading ? "Saving..." : editingId ? "Update Mock Test" : "Save Mock Test"}
            </button>
            {editingId && (
              <button
                type="button"
                className="rounded border border-gray-300 px-5 py-2 font-semibold"
                onClick={resetForm}
              >
                Cancel Edit
              </button>
            )}
          </div>
          </form>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-bgBlue">
                View Mock Tests
              </h3>
              <p className="text-sm text-gray-600">
                Total mock tests: {filteredMockTests.length}
              </p>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm md:w-80"
                placeholder="Search mock tests..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className="rounded border border-bgBlue px-4 py-2 text-sm font-semibold text-bgBlue"
                onClick={fetchMockTests}
              >
                Refresh
              </button>
            </div>
          </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-100 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Exam</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Questions</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Questions</th>
              <th className="px-4 py-3">Users</th>
              <th className="px-4 py-3">Leaderboard</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredMockTests.map((mockTest) => {
              const examName =
                getExamName(mockTest.examDetails) ||
                getExamName(mockTest.exam) ||
                examNameById[getId(mockTest.exam)] ||
                mockTest.course?.exams?.find((exam) => getId(exam) === getId(mockTest.exam))
                  ?.examName ||
                "-";

              return (
                <tr key={mockTest._id} className="border-t">
                  <td className="px-4 py-3 font-semibold">{mockTest.title}</td>
                  <td className="px-4 py-3">{mockTest.course?.planName || "-"}</td>
                  <td className="px-4 py-3">{examName}</td>
                  <td className="px-4 py-3">{mockTest.category}</td>
                  <td className="px-4 py-3">{mockTest.actualQuestionCount}</td>
                  <td className="px-4 py-3">{mockTest.durationMinutes} min</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        mockTest.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {mockTest.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      className="inline-block rounded bg-green-600 px-3 py-2 text-xs font-semibold text-white"
                      to={`/dash/mock-tests/${
                        courseId || mockTest.course?._id || mockTest.course
                      }/questions/${mockTest._id}`}
                    >
                      Add Question
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      className="inline-flex items-center gap-1 rounded bg-bgBlue px-3 py-2 text-xs font-semibold text-white"
                      to={`/dash/mock-tests/${mockTest._id}/results`}
                    >
                      <MdPeople />
                      Users
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      className="inline-flex items-center gap-1 rounded bg-purple px-3 py-2 text-xs font-semibold text-white"
                      to={`/dash/mock-tests/${mockTest._id}/leaderboard`}
                    >
                      <MdLeaderboard />
                      Leaderboard
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        className="rounded bg-bgBlue p-2 text-white"
                        onClick={() => handleEdit(mockTest)}
                        title="Edit"
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="rounded bg-red-600 p-2 text-white"
                        onClick={() => setDeleteMockTestId(mockTest._id)}
                        title="Delete"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!filteredMockTests.length && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan="11">
                  No mock tests found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        </div>
      )}
      <DeletePasswordModal
        isOpen={Boolean(deleteMockTestId)}
        onClose={() => setDeleteMockTestId(null)}
        onConfirm={() => handleDelete(deleteMockTestId)}
        title="Delete Mock Test"
        description="Password verify hone ke baad mock test delete hoga."
      />
    </div>
  );
}
